import React, { useState, useEffect, useContext } from "react";
import { useLocation, useHistory, matchPath } from "react-router-dom";
import _ from "lodash";
import "./style.css";

const basename = '/app'

const Context = React.createContext(null);

export const useSidebarContext = () => {
  return useContext(Context);
};

export const SidebarProvider = ({ children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Context.Provider value={{ open, setOpen }}>{children}</Context.Provider>
  );
};

export const SidebarToggler = () => {
  const { open, setOpen } = useSidebarContext();
  // "Collapse", not "collapse to icons": what collapsing leaves behind depends
  // on the viewport — an icon rail at md and up, nothing at all below it, where
  // the rail is already as narrow as the sidebar gets.
  const hint = open ? "Collapse the sidebar" : "Expand the sidebar";

  // Collapsing is a class on #sidebar, not a jQuery show()/hide(). Those wrote
  // an inline `display` that outranked both the .collapse classes the sidebar
  // carries and the small-screen rules in style.css, and hiding it outright
  // took the icons with it — there was nothing left to click but this toggler.
  return (
    <a
      href="#"
      aria-expanded={open}
      aria-label={hint}
      title={hint}
      onClick={(e) => {
        e.preventDefault();
        setOpen(!open);
        return false;
      }}
    >
      <i className="fa fa-navicon mx-2"></i>
    </a>
  );
};

export const Sidebar = function ({ menu, children }) {
  const history = useHistory();
  const sidebar = useSidebarContext();

  // The provider is optional — Navbar already guards on the context being
  // there — and without one there is nothing that can collapse the sidebar.
  const iconsOnly = sidebar ? !sidebar.open : false;

  return (
    <div className="container-fluid">
      <div className="row d-flex d-md-block flex-nowrap wrapper">
        <div
          className={
            "col-md-3 float-left col-1 pl-0 pr-0 collapse width show" +
            (iconsOnly ? " icons-only" : "")
          }
          id="sidebar"
        >
          <div className="list-group border-0 card text-center text-md-left">
            {_.map(menu, (item, index) => (
              <SidebarItem
                key={"sidebar_" + index}
                parent={"sidebar"}
                path={String(index)}
                {...item}
                history={history}
              />
            ))}
          </div>
        </div>
      </div>
      {/* sidebar-content: below md the rail is position:fixed, so it is out of
          flow and the content starts underneath it. style.css reserves its
          width here — the two sit side by side at every size. rail-hidden says
          the rail has slid off-canvas, so that reservation is given back. */}
      <div
        className={
          "row no-gutters sidebar-content" + (iconsOnly ? " rail-hidden" : "")
        }
      >
        <div className="col">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Is this item — or anything beneath it — the page being shown?
 *
 * A branch has to answer for its whole subtree, because nothing on the branch
 * itself matches the location that opens it: the site's submenu is revealed by
 * standing on `/site/x/page`, which is a leaf two levels down.
 *
 * `exact` defaults to whether the item is a leaf, which is what the flat menu
 * has always done. A branch is a prefix of everything under it, so matching it
 * exactly would close it the moment you clicked into one of its children. An
 * item that is BOTH a destination and a prefix of its siblings — a site's
 * Configuration, which lives at the site's own path — passes `exact` itself.
 */
const exactness = ({ exact, submenu }) =>
  exact === undefined ? !submenu || submenu.length === 0 : exact;

const isCurrent = (pathname, item) =>
  Boolean(item.to) &&
  Boolean(matchPath(pathname, { path: item.to, exact: exactness(item) }));

/**
 * "Inside" is a prefix where "current" is exact, and the two are not the same
 * question.
 *
 * Current is which single entry to highlight, so a leaf answers it exactly:
 * standing on a page is not standing on the list of them. Inside is whether to
 * show the branch at all, and standing on a page IS being inside Pages. Asking
 * `isCurrent` here left the submenu shut on every deep URL — it only looked
 * open because the effect below had opened it on the way in, so a bookmarked
 * page editor loaded with nothing in the shell naming the site.
 */
const isInside = (pathname, item) =>
  (Boolean(item.to) &&
    Boolean(matchPath(pathname, { path: item.to, exact: false }))) ||
  _.some(item.submenu, (child) => isInside(pathname, child));

export const SidebarItem = ({
  parent,
  path,
  icon,
  label,
  to,
  exact,
  submenu,
  history,
}) => {
  const menuId = `menu${path}`;
  const iconRes = icon ? <i className={"fa fa-" + icon}></i> : null;
  const leaf = !submenu || submenu.length === 0;

  const { pathname } = useLocation();

  const current = isCurrent(pathname, { to, exact, submenu });

  // A branch opens itself when the page you are on is inside it, and is yours
  // to open and close anywhere else. State rather than the location alone
  // because the two answer different questions: the location says which branch
  // you are working in, the toggle says how much of the rest you want to see.
  //
  // The effect only ever opens. Navigating into a branch has to reveal it —
  // otherwise the entry you just clicked disappears from under the pointer —
  // but nothing about being elsewhere should force it shut again.
  const inside = !leaf && isInside(pathname, { to, exact, submenu });
  const [open, setOpen] = useState(inside);

  useEffect(() => {
    if (inside) {
      setOpen(true);
    }
  }, [inside]);

  // title, on every branch below: collapsed to icons the label is the only
  // thing naming the destination, so it has to survive as a tooltip and as the
  // accessible name the hidden span stops supplying.
  if (leaf) {
    return (
      <a
        href={`${basename}${to}`}
        className={"list-group-item " + (current ? "" : "collapsed")}
        data-parent={"#" + parent}
        title={label}
        onClick={(e) => {
          e.preventDefault();
          history.push(to);
          return false;
        }}
      >
        {iconRes}{" "}
        <span
          className={
            "sidebar-label " +
            (parent === "sidebar" ? "d-none d-md-inline" : "inline")
          }
        >
          {label}
        </span>
      </a>
    );
  }

  const labelRes = (
    <>
      {iconRes}{" "}
      <span
        className={
          "sidebar-label " +
          (parent === "sidebar" ? "d-none d-md-inline" : "inline")
        }
      >
        {label}
      </span>
    </>
  );

  return (
    <>
      {/* Two controls on one row, not one that means two things. A branch that
          navigated AND toggled on the same click could never be closed while
          you stood inside it, and one that only toggled would leave the thing
          it names unreachable. So the label goes where it points and the caret
          opens what is under it.

          The caret is a <button>, and that is load-bearing rather than
          pedantic: e2e suites reach sidebar entries with
          getByRole('link', { name }), which is a SUBSTRING match, so a caret
          rendered as an anchor labelled "Expand Sites" would be a second
          match for "Sites" and fail the locator in strict mode. */}
      <div
        className={"list-group-item sidebar-branch " + (current ? "" : "collapsed")}
        data-parent={"#" + parent}
      >
        {to ? (
          <a
            href={`${basename}${to}`}
            className="sidebar-branch-link"
            title={label}
            onClick={(e) => {
              e.preventDefault();
              history.push(to);
              return false;
            }}
          >
            {labelRes}
          </a>
        ) : (
          // No destination of its own — it names what the entries under it
          // belong to, so the label toggles alongside the caret rather than
          // sitting there dead.
          <button
            type="button"
            className="sidebar-branch-link"
            title={label}
            onClick={() => setOpen(!open)}
          >
            {labelRes}
          </button>
        )}

        <button
          type="button"
          className="sidebar-caret"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
          onClick={() => setOpen(!open)}
        />
      </div>

      <div
        className={"collapse" + (open ? " show" : "")}
        id={menuId}
        data-parent={"#" + parent}
      >
        {_.map(submenu, (item, index) => (
          <SidebarItem
            key={`${menuId}_${index}`}
            parent={menuId}
            // Path-derived, not arithmetic on the indexes: "1" + "2" and
            // "12" + "" are the same string, so two different branches could
            // otherwise mint the same id and aria-controls would point at
            // whichever rendered last.
            path={`${path}-${index}`}
            {...item}
            history={history}
          />
        ))}
      </div>
    </>
  );
};
